import React, { FC, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FrozenSalesWeekly } from "interface/sales";
import { GrideDateFormatter } from "ui/Date";

interface FrozenWeeklySalesMobileListProps {
	items: FrozenSalesWeekly[];
	loading: boolean;
	onLoadMore: () => void;
}

const formatWeight = (value?: number | null) => {
	const asNumber = Number(value || 0);
	return `${asNumber.toLocaleString("no-NO", {
		maximumFractionDigits: 2,
	})} kg`;
};

const formatCurrency = (value?: number | null) => {
	const asNumber = Number(value || 0);
	return asNumber.toLocaleString("no-NO", {
		maximumFractionDigits: 2,
		style: "currency",
		currency: "NOK",
	});
};

const FrozenWeeklySalesMobileList: FC<FrozenWeeklySalesMobileListProps> = ({
	items,
	loading,
	onLoadMore,
}) => {
	const { t } = useTranslation();

	const empty = useMemo(
		() => !loading && items.length === 0,
		[items.length, loading],
	);

	useEffect(() => {
		const handleWindowScroll = () => {
			const remaining =
				document.documentElement.scrollHeight -
				window.innerHeight -
				window.scrollY;

			if (remaining < 200) {
				onLoadMore();
			}
		};

		window.addEventListener("scroll", handleWindowScroll, { passive: true });
		handleWindowScroll();

		return () => {
			window.removeEventListener("scroll", handleWindowScroll);
		};
	}, [onLoadMore, items.length, loading]);

	return (
		<div className="frozen-sales-list-mobile">
			{empty && (
				<div className="frozen-sales-list-mobile-empty">{t("NoRows")}</div>
			)}

			{items.map((item, index) => (
				<div
					key={`${item.vesselName || "noname"}-${index}`}
					className="frozen-sales-list-card"
				>
					<div className="frozen-sales-list-card-header">
						<div className="frozen-sales-list-card-group">
							{item.vesselName || "-"}
						</div>
						<div className="frozen-sales-list-card-date">
							{item.salesDate ? (
								<GrideDateFormatter
									date={item.salesDate as any}
									formatType="dd.MM.yyyy"
									noTd={true}
								/>
							) : (
								"-"
							)}
						</div>
					</div>

					<div className="frozen-sales-list-card-body">
						<div className="frozen-sales-list-row">
							<div className="frozen-sales-list-row-main">
								<div className="frozen-sales-list-row-title">
									{item.productText || "-"}
								</div>
							</div>
							<div className="frozen-sales-list-row-metrics">
								<div>
									<span>{t("Weight")}</span>
									<strong>{formatWeight(item.netLiveWeightKg)}</strong>
								</div>
								<div>
									<span>{t("Price")}</span>
									<strong>{formatCurrency(item.price)}</strong>
								</div>
								<div>
									<span>{t("AveragePrice")}</span>
									<strong>{formatCurrency(item.averagePrice)}</strong>
								</div>
							</div>
							<div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
								<span style={{ fontSize: "12px", color: "#666666", lineHeight: 1.2 }}>{t("BuyerName")}</span>
								<strong style={{ fontSize: "13px", color: "#232626", lineHeight: 1.3 }}>{item.buyerName || "-"}</strong>
							</div>
							{(item.salesWeekDay || item.msc) && (
								<div className="frozen-sales-list-row-metrics">
									<div>
										<span>{t("Information")}</span>
										<strong>{item.information || "-"}</strong>
									</div>
									<div>
										<span>{t("WeekDay")}</span>
										<strong>{item.salesWeekDay || "-"}</strong>
									</div>
									<div>
										<span>MSC</span>
										<strong>{item.msc || "-"}</strong>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			))}

			{loading && (
				<div className="frozen-sales-list-mobile-loading">{t("Loading")}</div>
			)}
		</div>
	);
};

export default FrozenWeeklySalesMobileList;